import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, LayoutDashboard, LogOut, Send, Bot, BotOff, KeyRound, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import api from "@/lib/api";

const Metric = ({ label, value }) => (
  <div className="bg-surface border border-black/10 p-6">
    <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673]">{label}</p>
    <p className="font-display font-black text-4xl mt-3 text-[#0E1E2E]">{value}</p>
  </div>
);

const Conversations = () => {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const loadConvs = useCallback(() => api.get("/admin/conversations").then((r) => setConvs(r.data)), []);
  const loadMessages = useCallback(async (id) => {
    const { data } = await api.get(`/admin/conversations/${id}/messages`);
    setMessages(data.messages); setConv(data.conversation);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
  }, []);

  useEffect(() => { loadConvs(); const iv = setInterval(loadConvs, 5000); return () => clearInterval(iv); }, [loadConvs]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active);
    const iv = setInterval(() => loadMessages(active), 4000);
    return () => clearInterval(iv);
  }, [active, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    await api.post(`/admin/conversations/${active}/reply`, { text: t });
    loadMessages(active); loadConvs();
  };
  const toggleAi = async () => {
    const { data } = await api.post(`/admin/conversations/${active}/toggle-ai`);
    setConv((c) => ({ ...c, ai_enabled: data.ai_enabled }));
    toast.success(`AI auto-reply ${data.ai_enabled ? "enabled" : "disabled"}`);
  };

  return (
    <div data-testid="admin-conversations" className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[70vh]">
      <div className="border border-black/10 bg-surface overflow-y-auto">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] p-4 border-b border-black/10">Conversations ({convs.length})</p>
        {convs.map((c) => (
          <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => setActive(c.id)}
            className={`w-full text-left p-4 border-b border-black/10 hover:bg-surface2 transition-colors ${active === c.id ? "bg-surface2 border-l-2 border-l-brand" : ""}`}>
            <div className="flex justify-between items-center">
              <span className="font-display font-bold text-sm text-[#0E1E2E]">{c.name}</span>
              {c.unread_admin > 0 && <span className="bg-brand text-[#06111C] text-[10px] font-mono2 px-1.5">{c.unread_admin}</span>}
            </div>
            <p className="text-[#556673] text-xs mt-1 truncate">{c.last_message}</p>
          </button>
        ))}
        {convs.length === 0 && <p className="text-[#8797A3] text-sm p-4">No conversations yet.</p>}
      </div>

      <div className="md:col-span-2 border border-black/10 bg-surface flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-[#8797A3] font-mono2 text-xs uppercase tracking-[0.2em]">Select a conversation</div>
        ) : (
          <>
            <div className="flex items-center justify-between p-4 border-b border-black/10">
              <span className="font-display font-bold text-[#0E1E2E]">{conv?.name || "Visitor"}</span>
              <button data-testid="toggle-ai-btn" onClick={toggleAi}
                className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.15em] border border-black/15 px-3 py-1.5 text-[#0E1E2E] hover:bg-brand hover:text-[#06111C] hover:border-brand transition-colors">
                {conv?.ai_enabled ? <><Bot size={14} /> AI On</> : <><BotOff size={14} /> AI Off</>}
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              {messages.map((m) => (
                <div key={m.id} className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.sender === "user" ? "mr-auto bg-surface2 text-[#22333F]" :
                  m.sender === "admin" ? "ml-auto bg-white text-[#06111C]" : "ml-auto bg-brand text-[#06111C]"}`}>
                  <span className="block font-mono2 text-[9px] uppercase tracking-widest opacity-70 mb-1">
                    {m.sender === "user" ? "Visitor" : m.sender === "admin" ? "You" : "UMG AI"}
                  </span>
                  {m.text}
                </div>
              ))}
            </div>
            <form onSubmit={send} className="border-t border-black/10 p-3 flex gap-2">
              <input data-testid="admin-reply-input" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Reply as UMG Team…" className="flex-1 bg-transparent text-sm text-[#0E1E2E] placeholder:text-[#8797A3] py-2" />
              <button data-testid="admin-reply-send" type="submit" className="w-9 h-9 bg-brand text-[#06111C] flex items-center justify-center"><Send size={15} /></button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it next time you sign in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((x) => x?.msg || "").filter(Boolean).join(" ")
        : "Could not update password";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings" className="max-w-md">
      <div className="bg-surface border border-black/10 p-6">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673] mb-6">Change Admin Password</p>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <input data-testid="current-password-input" type="password" required value={current}
            onChange={(e) => setCurrent(e.target.value)} placeholder="Current password"
            className="bg-transparent border border-black/15 px-3 py-2.5 text-sm text-[#0E1E2E] placeholder:text-[#8797A3]" />
          <input data-testid="new-password-input" type="password" required minLength={8} value={next}
            onChange={(e) => setNext(e.target.value)} placeholder="New password (min 8 characters)"
            className="bg-transparent border border-black/15 px-3 py-2.5 text-sm text-[#0E1E2E] placeholder:text-[#8797A3]" />
          <input data-testid="confirm-password-input" type="password" required minLength={8} value={confirm}
            onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password"
            className="bg-transparent border border-black/15 px-3 py-2.5 text-sm text-[#0E1E2E] placeholder:text-[#8797A3]" />
          <button data-testid="change-password-submit" type="submit" disabled={saving}
            className="bg-brand text-[#06111C] font-mono2 text-[11px] uppercase tracking-[0.15em] px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

const BookOrders = () => {
  const [orders, setOrders] = useState([]);

  const load = useCallback(() => api.get("/admin/book-orders").then((r) => setOrders(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const ship = async (id) => {
    await api.post(`/admin/book-orders/${id}/ship`);
    toast.success("Order marked as shipped");
    load();
  };

  return (
    <div data-testid="admin-orders" className="flex flex-col gap-4">
      {orders.length === 0 && <p className="text-[#8797A3] text-sm">No book orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-${o.id}`} className="bg-surface border border-black/10 p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="text-sm">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-display font-bold text-[#0E1E2E]">{o.customer_name}</span>
              <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#8797A3]">#{o.id}</span>
              <span className={`font-mono2 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${
                o.payment_status === "paid" ? "bg-brand/15 text-brand" : "bg-black/5 text-[#8797A3]"}`}>{o.payment_status}</span>
              <span className={`font-mono2 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${
                o.status === "shipped" ? "bg-[#0E1E2E] text-white" : "bg-black/5 text-[#556673]"}`}>{o.status.replace("_", " ")}</span>
            </div>
            <p className="text-[#556673] mt-2">{o.book} — ${Number(o.amount_total).toFixed(2)} · {o.email}</p>
            <p className="text-[#8797A3] text-xs mt-1">
              Ship to: {o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}, {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}, {o.shipping_address.country}
            </p>
            <p className="text-[#8797A3] text-xs mt-1">{new Date(o.created_at).toLocaleString()}</p>
          </div>
          {o.payment_status === "paid" && o.status !== "shipped" && (
            <button data-testid={`ship-order-${o.id}`} onClick={() => ship(o.id)}
              className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.15em] border border-black/15 px-4 py-2.5 text-[#0E1E2E] hover:bg-brand hover:text-[#06111C] hover:border-brand transition-colors whitespace-nowrap">
              <Package size={14} /> Mark Shipped
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, [tab]);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const tabs = [
    { k: "overview", label: "Overview", icon: LayoutDashboard },
    { k: "conversations", label: "Live Chat", icon: MessageSquare },
    { k: "orders", label: "Orders", icon: Package },
    { k: "settings", label: "Settings", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-[#F1F4F3] flex flex-col md:flex-row">
      <aside className="md:w-64 md:h-screen md:sticky top-0 bg-[#F1F4F3] border-b md:border-b-0 md:border-r border-black/10 p-6 flex md:flex-col justify-between">
        <div>
          <div className="mb-10"><Brand size={30} /></div>
          <nav className="flex md:flex-col gap-1">
            {tabs.map((t) => (
              <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`flex items-center gap-3 px-4 py-3 font-mono2 text-xs uppercase tracking-[0.15em] transition-colors ${tab === t.k ? "bg-surface text-brand" : "text-[#556673] hover:text-[#0E1E2E]"}`}>
                <t.icon size={15} /> <span className="hidden md:inline">{t.label}</span>
              </button>
            ))}
          </nav>
        </div>
        <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-3 px-4 py-3 font-mono2 text-xs uppercase tracking-[0.15em] text-[#556673] hover:text-brand transition-colors">
          <LogOut size={15} /> <span className="hidden md:inline">Logout</span>
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-12">
        <div className="flex justify-between items-center mb-10">
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#556673]">Dashboard</p>
            <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight capitalize text-[#0E1E2E]">{tab}</h1>
          </div>
          <span className="font-mono2 text-xs text-[#8797A3] hidden md:block">{user?.email}</span>
        </div>

        {tab === "overview" && stats && (
          <div data-testid="admin-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="Conversations" value={stats.conversations} />
            <Metric label="Open Chats" value={stats.open_conversations} />
            <Metric label="Ebooks Live" value={4} />
            <Metric label="Shows" value={2} />
          </div>
        )}
        {tab === "conversations" && <Conversations />}
        {tab === "orders" && <BookOrders />}
        {tab === "settings" && <ChangePassword />}
      </main>
    </div>
  );
}
